<script>
  import cloneDeep from 'lodash.clonedeep'
  import isEqual from 'lodash.isequal'

  import FormCollection from './FormCollection'

  import BlockLayout from './layouts/BlockLayout'
  import InlineLayout from './layouts/InlineLayout'
  import ColumnLayout from './layouts/ColumnLayout'
  import PanelLayout from './layouts/PanelLayout'

  import { getConfigFromSlot, stdConfig } from './libs/stdConfig'
  import getNodes from './libs/getNodes'

  const layouts = {
    BlockLayout,
    InlineLayout,
    ColumnLayout,
    PanelLayout,
  }

  export default {
    components: {
      FormCollection,
    },

    provide() {
      return {
        components: this.mergedComponents,
        formItems: this.mergedFormItems,
        layouts: this.mergedLayouts,

        validationMessages: this.validationMessages,
        validationRules: this.validationRules,

        handleFormMemberCreated: this.handleFormMemberCreated,
        handleFormMemberDestroyed: this.handleFormMemberDestroyed,
      }
    },

    props: {
      modelValue: {
        type: [Object, Array],
        default: undefined,
      },
      config: {
        type: Object,
        default: undefined,
      },
      readonly: {
        type: Boolean,
        required: false,
        default: undefined,
      },

      submitText: {
        type: String,
        default: '提交',
      },
      resetText: {
        type: String,
        default: '重置',
      },
      submit: {
        type: Function, // 返回 promise
        default: undefined,
      },

      validationMessages: {
        type: Object,
        default: undefined,
      },
      validationRules: {
        type: Object,
        default: undefined,
      },

      components: {
        type: Object,
        default: undefined,
      },
      formItems: {
        type: Object,
        default: undefined,
      },
      layouts: {
        type: Object,
        default: undefined,
      },
    },

    emits: ['update:modelValue', 'input', 'is-dirty-change', 'is-valid-change'],

    data() {
      let config

      if (typeof this.config === 'object') {
        config = this.config
      } else {
        config = getConfigFromSlot({
          children: this.$slots.default(),
        })
      }

      const { type, children } = config
      let model

      if ((type === 'object' || type === undefined) && typeof this.modelValue !== 'object') {
        model = {}
      } else if ((type === 'list' || type === 'array') && !Array.isArray(this.modelValue)) {
        model = new Array(children.length).fill(undefined)
      } else {
        model = cloneDeep(this.modelValue)
      }

      return {
        model,
        formMemberVms: [],
        isDirty: false,
        isSubmitting: false,
      }
    },

    computed: {
      mergedLayouts() {
        return {
          ...layouts,
          ...this.layouts,
        }
      },

      slotConfig() {
        if (this.config) {
          return undefined
        }

        return getConfigFromSlot({
          children: this.$slots.default(),
        })
      },

      standardConfig() {
        return stdConfig(this.config ?? this.slotConfig, this.readonly)
      },

      nodes() {
        return getNodes(this.standardConfig)
      },

      isValid() {
        return this.formMemberVms.every(formMemberVm => formMemberVm.isValid === true)
      },
    },

    watch: {
      modelValue: {
        deep: true,
        handler(value) {
          if (isEqual(value, this.model)) {
            return
          }

          this.model = cloneDeep(value)
        },
      },

      model: {
        deep: true,
        handler(model) {
          if (isEqual(model, this.modelValue)) {
            return
          }

          this.$emit('update:modelValue', cloneDeep(model))
          this.$emit('is-dirty-change', true)
          this.isDirty = true
        },
      },

      isValid: {
        handler(value) {
          this.$emit('is-valid-change', value)
        },
        immediate: true,
      },

      nodes(newValue, oldValue) {
        if (newValue.length === oldValue.length) {
          return
        }

        throw new Error('[VueFormily] Add or remove form member from config is not allowed, toggle applying form member by `show` instead ')
      },
    },

    created() {
      this.initValue = cloneDeep(this.model)
    },

    methods: {
      // Public
      validate() {
        return Promise.all(this.formMemberVms.map(formMemberVm => formMemberVm.validate()))
      },

      handleFormMemberCreated(vm) {
        this.formMemberVms.push(vm)
      },

      handleFormMemberDestroyed(vm) {
        const index = this.formMemberVms.findIndex(formMemberVm => formMemberVm === vm)

        this.formMemberVms.splice(index, 1)
      },

      async handleSubmit() {
        this.isSubmitting = true

        try {
          await this.validate()
          this.submit(this.model)
          this.isDirty = false
        } catch (error) {
          console.log(error) // eslint-disable-line
        }

        this.isSubmitting = false
      },

      handleReset() {
        if (isEqual(this.initValue, this.model)) {
          return
        }

        this.model = cloneDeep(this.initValue)
      },
      handleChange(v) {
        this.model = v
      },
    },
  }
</script>

<template>
  <form-collection
    :value="model"
    :config="standardConfig"
    @input="handleChange"
  >
    <div
      v-if="submit"
      class="operation-patch"
    >
      <slot name="submit-button">
        <submit-button
          :disabled="!isDirty || isSubmitting"
          @click="handleSubmit"
        >
          {{ submitText }}
        </submit-button>
      </slot>

      <slot name="reset-button">
        <reset-button @click="handleReset">
          {{ resetText }}
        </reset-button>
      </slot>
    </div>
  </form-collection>
</template>

<style lang="stylus" scoped>
  .operation-patch
    padding-bottom 24px
</style>
